import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Divider,
  Container,
  IconButton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Edit,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import seoService from './seo.service';
import type { MetaTag, UpdateMetaTagRequest } from './seo.dto';

const EditSEO = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [metaTag, setMetaTag] = useState<MetaTag | null>(null);
  const [formData, setFormData] = useState<
    UpdateMetaTagRequest & { page_slug?: string }
  >({
    page_slug: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: '',
  });

  useEffect(() => {
    const fetchMetaTag = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);
        const data = await seoService.getMetaTagById(token, parseInt(id));
        setMetaTag(data);
        setFormData({
          page_slug: data.page_slug || '',
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          meta_keywords: data.meta_keywords || '',
        });
      } catch (err) {
        console.error('Error fetching meta tag:', err);
        setSnackbar({
          open: true,
          message:
            err instanceof Error ? err.message : 'Failed to fetch meta tag',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMetaTag();
  }, [token, id]);

  const handleInputChange = (
    field: keyof UpdateMetaTagRequest,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !id) {
      setSnackbar({
        open: true,
        message: 'Authentication required',
        severity: 'error',
      });
      return;
    }

    if (!formData.page_slug || !formData.meta_title) {
      setSnackbar({
        open: true,
        message: 'Page slug and meta title are required',
        severity: 'error',
      });
      return;
    }

    try {
      setSaving(true);
      await seoService.updateMetaTag(token, parseInt(id), formData);

      setSnackbar({
        open: true,
        message: 'Meta tag updated successfully',
        severity: 'success',
      });

      // Navigate back to SEO list after a short delay
      setTimeout(() => {
        navigate('/seo');
      }, 1500);
    } catch (err) {
      console.error('Error updating meta tag:', err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to update meta tag',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCancel = () => {
    navigate('/seo');
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading meta tag data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!metaTag) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Meta tag not found
          </Alert>
          <Button onClick={handleCancel}>Back to SEO Management</Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate('/seo')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Meta Tag
              </Typography>
            </Box>
            <Chip
              label={metaTag.page_slug}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update SEO meta tag information and settings
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Page Information Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Page Information
                </Typography>
                <TextField
                  fullWidth
                  required
                  label="Page Slug"
                  value={formData.page_slug || metaTag.page_slug}
                  onChange={e => handleInputChange('page_slug', e.target.value)}
                  placeholder="e.g., home, about-us"
                  helperText="Unique page identifier (lowercase, hyphens)"
                  margin="normal"
                />
                {metaTag.created_at && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Created on
                    </Typography>
                    <Typography variant="h6">
                      {new Date(metaTag.created_at).toLocaleDateString()}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* SEO Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        SEO Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Meta Title"
                        value={formData.meta_title}
                        onChange={e =>
                          handleInputChange('meta_title', e.target.value)
                        }
                        helperText="50-60 characters recommended"
                        inputProps={{ maxLength: 60 }}
                        placeholder="e.g., Clear Scan - Medical Diagnostic Services"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Meta Description"
                        multiline
                        rows={3}
                        value={formData.meta_description}
                        onChange={e =>
                          handleInputChange('meta_description', e.target.value)
                        }
                        helperText="150-160 characters recommended"
                        inputProps={{ maxLength: 160 }}
                        placeholder="Brief description of the page content..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Keywords
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Meta Keywords"
                        value={formData.meta_keywords}
                        onChange={e =>
                          handleInputChange('meta_keywords', e.target.value)
                        }
                        placeholder="keyword1, keyword2, keyword3"
                        helperText="Comma-separated keywords"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <SaveIcon />
                      }
                      disabled={
                        saving || !formData.page_slug || !formData.meta_title
                      }
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditSEO;
